import { NavLink, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import navs from 'config/navs'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Icon } from 'components/shared'
import { classNameJoin } from 'utils/lib'
import { useCategoryUnreadCount } from 'contexts/CategoryUnreadCountProvider'
import env from 'config/env'
import { Fragment } from 'react'

interface IProps extends IStyleProps {
    onToggle: () => void
}

interface IStyleProps {
    toggle: boolean
}

const GlobalNavigationBar = ({ toggle, onToggle }: IProps) => {
    const { pathname } = useLocation()
    const domain = pathname.split('/')[1]
    const { categoryUnreadCount } = useCategoryUnreadCount()

    return (
        <>
            <Background toggle={toggle} onClick={onToggle} />
            <GNB toggle={toggle}>
                <div className="h-full w-[230px] py-2 space-y-4 bg-white border-r border-secondary-900">
                    {navs.map((nav, i) => (
                        <div className="relative px-3" key={i}>
                            <div className="pt-1 pb-3 text-xs text-secondary-400">{nav.title}</div>
                            <ul>
                                {nav.items.map((nav) => {
                                    const { path, name, icon, basename, active } = nav
                                    const isOrderManagement = name === '주문관리'
                                    const isFeedback = name === '의견 보내기'
                                    const isChatList = name === '문의톡 관리'

                                    if (isOrderManagement || isFeedback) {
                                        return (
                                            <li key={path} onClick={onToggle}>
                                                <NavLink
                                                    to={isOrderManagement ? '/live/list' : path}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <div className="flex items-center h-9 px-2 rounded-md text-secondary-500">
                                                        <Icon
                                                            className="w-6 text-center"
                                                            icon={icon as IconProp}
                                                        />
                                                        <span className="ml-3 text-sm">{name}</span>
                                                    </div>
                                                </NavLink>
                                            </li>
                                        )
                                    }

                                    if (path) {
                                        return (
                                            <Fragment key={path}>
                                                <li onClick={onToggle}>
                                                    <NavLink
                                                        className={({ isActive }) =>
                                                            classNameJoin([
                                                                'flex items-center h-9 px-2',
                                                                // 자식 노드가 없는 경우에만 백그라운드 색상 적용
                                                                (active?.includes(pathname) ||
                                                                    path.includes(domain)) &&
                                                                (!nav.children ||
                                                                    nav.children.length === 0)
                                                                    ? 'bg-primary-950'
                                                                    : '',
                                                                // 활성 상태인 경우 텍스트 색상 변경
                                                                isActive ||
                                                                path.includes(domain) ||
                                                                active?.includes(pathname)
                                                                    ? ' text-accect font-medium'
                                                                    : 'text-secondary-500',
                                                            ])
                                                        }
                                                        to={path}
                                                    >
                                                        <Icon
                                                            className="w-6 text-center"
                                                            icon={icon as IconProp}
                                                        />
                                                        <span className="ml-3 text-sm">{name}</span>
                                                        {isChatList &&
                                                            categoryUnreadCount.total > 0 && (
                                                                <span className="ml-auto w-[40px] h-[20px] bg-accect text-white flex items-center mt-0.5 justify-center pr-[1px] pb-[1px] rounded-xl text-sm">
                                                                    {categoryUnreadCount.total > 999
                                                                        ? '999+'
                                                                        : categoryUnreadCount.total}
                                                                </span>
                                                            )}
                                                    </NavLink>
                                                </li>
                                                {(active?.includes(pathname) ||
                                                    domain === basename) &&
                                                    nav?.children &&
                                                    nav.children.length > 0 && (
                                                        <ul className="py-1.5">
                                                            {nav.children.map(
                                                                ({ path, name, active }) => (
                                                                    <li
                                                                        key={path}
                                                                        onClick={onToggle}
                                                                    >
                                                                        <NavLink
                                                                            className={classNameJoin(
                                                                                [
                                                                                    'block pl-12 py-2 text-sm rounded-md',
                                                                                    // 활성 상태인 경우 텍스트 & 배경 색상 변경
                                                                                    path &&
                                                                                    ((active &&
                                                                                        active.test(
                                                                                            pathname
                                                                                        )) ||
                                                                                        pathname ===
                                                                                            path)
                                                                                        ? 'text-accect bg-primary-950'
                                                                                        : 'text-secondary-500',
                                                                                ]
                                                                            )}
                                                                            to={path}
                                                                        >
                                                                            {name}
                                                                        </NavLink>
                                                                    </li>
                                                                )
                                                            )}
                                                        </ul>
                                                    )}
                                            </Fragment>
                                        )
                                    }

                                    return null
                                })}
                            </ul>
                        </div>
                    ))}

                    {env.NODE_ENV === 'development' && (
                        <div className="relative px-3">
                            <div className="pt-1 pb-3 text-xs text-secondary-400">실험실</div>
                            <ul>
                                <li>
                                    <NavLink
                                        className={({ isActive }) =>
                                            classNameJoin([
                                                'flex items-center h-9 px-2 rounded-md',
                                                isActive || '/toss'.includes(domain)
                                                    ? ' text-accect bg-primary-950 font-medium'
                                                    : 'text-secondary-500',
                                            ])
                                        }
                                        to={`/toss`}
                                    >
                                        <Icon
                                            className="w-6 text-center"
                                            icon={['far', 'credit-card']}
                                        />
                                        <span className="ml-3 text-sm">Toss 결제</span>
                                    </NavLink>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </GNB>
        </>
    )
}

const GNB = styled.nav<IStyleProps>`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    transition: all 0.3s;
    z-index: 999;

    transform: translateX(${(props) => (props.toggle ? '0px' : '-230px')});

    @media (min-width: 1024px) {
        transform: translateX(0px);
    }
`

const Background = styled.div<IStyleProps>`
    cursor: pointer;
    position: fixed;
    background-color: rgba(0, 0, 0, 0.6);
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    z-index: 998;
    transition: all 0.3s;
    visibility: ${(props) => (props.toggle ? 'visible' : 'hidden')};
    opacity: ${(props) => (props.toggle ? 1 : 0)};

    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    @media (min-width: 1024px) {
        display: none;
    }
`

export default GlobalNavigationBar
